/**
 * The words and small rules the Automations list and one automation share, whichever layout draws them.
 */
import { openedShare } from "../../shared/automation-words";
import type { AutomationDetail, AutomationListResponse, AutomationStatus, AutomationSummary, PersonAtBlock } from "../../shared/types";
import { type Editor, kindLabel, untilWords } from "../components/FlowEditor";
import { ApiError } from "../lib/api";
import { flatten, graphToTree, locateStep } from "./flow-model";

export const fmt = (n: number) => n.toLocaleString("en-US");
export const people = (n: number) => `${fmt(n)} ${n === 1 ? "person" : "people"}`;
export const errorText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

/** On, but every room it is on for is still a draft, so nothing runs until one is published. */
export function waitingRoom<R extends { enabled: boolean; draft?: boolean }>(status: AutomationStatus, rooms: R[]): R | null {
  const on = rooms.filter((r) => r.enabled);
  return status === "on" && on.length > 0 && on.every((r) => r.draft) ? on[0] : null;
}

/** Where a person in it is: waiting until when, or the step they are at. */
export function whereWords(p: PersonAtBlock, node: string, editor: Editor): string {
  if (node === "start") return "Just joined";
  const at = locateStep(editor.steps, node);
  if (at?.step.type === "wait" && p.wakes_at) return `Waiting ${untilWords(p.wakes_at)}`;
  return at ? `At ${kindLabel(at.step.type)}` : "In it";
}

/** The one plain line under a name, drawn from its own start and emails. */
export function plainLine(a: Pick<AutomationSummary, "trigger" | "graph">): string {
  const when = `When someone ${a.trigger.type === "tag_removed" ? "loses" : "gets"} the tag ${a.trigger.value}`;
  const emails = flatten(graphToTree(a.graph)).filter((s) => s.type === "email").length;
  return emails ? `${when}, ${emails} ${emails === 1 ? "email" : "emails"}` : when;
}

/** The rooms an automation runs on, as one line. */
export function roomsLine(rooms: AutomationSummary["rooms"]): string {
  if (!rooms.length) return "Not on a room yet";
  if (rooms.length <= 2) return rooms.map((r) => r.title).join(", ");
  return `${rooms[0].title} and ${rooms.length - 1} more`;
}

/** How it's doing: joined, opened, became customers, each with its bar's width. Null before it has emails and runs. */
export function howRows(d: Pick<AutomationDetail, "enroll" | "status" | "graph">): Array<[label: string, value: string, width: number]> | null {
  const e = d.enroll;
  if (!e || d.status === "draft" || !d.graph.nodes.some((n) => n.type === "email")) return null;
  const opened = openedShare(e) ?? 0;
  const share = e.entered ? (e.signed / e.entered) * 100 : 0;
  return [
    ["Joined", fmt(e.entered), 100],
    ["Opened", `${opened}%`, opened],
    ["Became customers", fmt(e.signed), Math.max(3, share)],
  ];
}

/**
 * A business with its first room made and nobody in its follow-ups yet: the follow-ups are ready, so the next
 * step is to bring people. Null otherwise.
 */
export function firstRoomOnly(data: AutomationListResponse): string | null {
  if (data.people_now > 0 || data.automations.some((a) => a.enroll)) return null;
  const rooms = new Set(data.automations.flatMap((a) => a.rooms.map((r) => r.webinar_id)));
  return rooms.size === 1 ? [...rooms][0] : null;
}
